import React, { useState, useEffect } from 'react';
import { Download, Plus, Trash, DragHorizontal, Save, Clock, Calendar, FileAudio } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fetchWithAuth, EVENTS_API } from '../../../utils/api';

const ScheduleTab = ({ routines, registrations, event, loading, userDetails = {} }) => {
  const [schedules, setSchedules] = useState([]);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [showNewScheduleForm, setShowNewScheduleForm] = useState(false);
  const [scheduledRoutines, setScheduledRoutines] = useState([]);
  const [availableRoutines, setAvailableRoutines] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Debug logging
  useEffect(() => {
    console.log('ScheduleTab - User Details:', userDetails);
  }, [userDetails]);
  
  // Helper function to get user's full name
  const getUserFullName = (userId) => {
    if (!userId) return 'No User ID';
    
    // First try to find the registration for this userId
    const registration = registrations.find(reg => reg.userId === userId);
    if (registration && (registration.userFirstName || registration.userLastName)) {
      console.log(`Using user details from registration: ${registration.userFirstName} ${registration.userLastName}`);
      return `${registration.userFirstName || ''} ${registration.userLastName || ''}`.trim();
    }
    
    console.log(`Getting full name for userId: ${userId}`);
    console.log(`User details for ${userId}:`, userDetails[userId]);
    
    const user = userDetails[userId];
    if (!user) {
      console.log(`No user details found for userId: ${userId}`);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Check if user is a string (might be an error message)
    if (typeof user === 'string') {
      console.log(`User details for ${userId} is a string:`, user);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Try to extract name from different possible structures
    let firstName = '';
    let lastName = '';
    let email = '';
    
    // Direct properties
    if (user.firstName) firstName = user.firstName;
    if (user.lastName) lastName = user.lastName;
    if (user.email) email = user.email;
    
    // Nested properties
    if (user.user) {
      if (user.user.firstName) firstName = user.user.firstName;
      if (user.user.lastName) lastName = user.user.lastName;
      if (user.user.email) email = user.user.email;
    }
    
    // Attributes property (common in Cognito user objects)
    if (user.attributes) {
      if (user.attributes.given_name) firstName = user.attributes.given_name;
      if (user.attributes.family_name) lastName = user.attributes.family_name;
      if (user.attributes.email) email = user.attributes.email;
    }
    
    // Build the full name
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (email) {
      return email;
    }
    
    return ''; // Return empty string if no name or email found
  };
  
  // Initialize available routines when component loads or routines change
  useEffect(() => {
    if (routines.length > 0) {
      setAvailableRoutines(routines.map(routine => ({
        ...routine,
        order: null
      })));
    }
  }, [routines]);
  
  // Load schedules for this event
  useEffect(() => {
    const loadSchedules = async () => {
      if (!event) return;
      
      try {
        // In a real implementation, you would fetch schedules from your API
        // For now, we'll check if the event has schedules property
        if (event.schedules) {
          setSchedules(event.schedules);
          if (event.schedules.length > 0) {
            setCurrentSchedule(event.schedules[0].id);
          }
        } else {
          setSchedules([]);
          setCurrentSchedule(null);
        }
      } catch (err) {
        console.error('Error loading schedules:', err);
      }
    };
    
    loadSchedules();
  }, [event]);
  
  // Create a new schedule
  const createSchedule = async () => {
    if (!newScheduleName.trim()) return;
    
    const newSchedule = {
      id: `schedule-${Date.now()}`,
      name: newScheduleName.trim(),
      date: new Date().toISOString(),
      routines: []
    };
    
    try {
      // In a real implementation, you would save this to your API
      const updatedSchedules = [...schedules, newSchedule];
      setSchedules(updatedSchedules);
      setCurrentSchedule(newSchedule.id);
      setNewScheduleName('');
      setShowNewScheduleForm(false);
      
      // Save to event
      await saveSchedulesToEvent(updatedSchedules);
    } catch (err) {
      console.error('Error creating schedule:', err);
    }
  };
  
  // Delete a schedule
  const deleteSchedule = async (scheduleId) => {
    // Replace direct use of confirm with a custom approach
    // This is just a simple implementation - you might want to use a modal dialog in production
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        const updatedSchedules = schedules.filter(s => s.id !== scheduleId);
        setSchedules(updatedSchedules);
        
        if (currentSchedule === scheduleId) {
          setCurrentSchedule(updatedSchedules.length > 0 ? updatedSchedules[0].id : null);
        }
        
        // Save to event
        await saveSchedulesToEvent(updatedSchedules);
      } catch (err) {
        console.error('Error deleting schedule:', err);
      }
    }
  };
  
  // Add a routine to the schedule
  const addRoutineToSchedule = (routineId) => {
    const routine = availableRoutines.find(r => r.id === routineId);
    if (!routine) return;
    
    // Add to scheduled routines
    const updatedScheduledRoutines = [
      ...scheduledRoutines,
      {
        ...routine,
        order: scheduledRoutines.length + 1
      }
    ];
    setScheduledRoutines(updatedScheduledRoutines);
    
    // Remove from available routines
    setAvailableRoutines(availableRoutines.filter(r => r.id !== routineId));
    
    // Update schedule
    updateScheduleRoutines(updatedScheduledRoutines);
  };
  
  // Remove a routine from the schedule
  const removeRoutineFromSchedule = (routineId) => {
    const routine = scheduledRoutines.find(r => r.id === routineId);
    if (!routine) return;
    
    // Remove from scheduled routines
    const updatedScheduledRoutines = scheduledRoutines.filter(r => r.id !== routineId);
    
    // Reorder remaining routines
    updatedScheduledRoutines.forEach((r, index) => {
      r.order = index + 1;
    });
    
    setScheduledRoutines(updatedScheduledRoutines);
    
    // Add back to available routines
    setAvailableRoutines([...availableRoutines, { ...routine, order: null }]);
    
    // Update schedule
    updateScheduleRoutines(updatedScheduledRoutines);
  };
  
  // Move a routine up in the schedule
  const moveRoutineUp = (index) => {
    if (index === 0) return;
    
    const updatedRoutines = [...scheduledRoutines];
    const temp = updatedRoutines[index];
    updatedRoutines[index] = updatedRoutines[index - 1];
    updatedRoutines[index - 1] = temp;
    
    // Update order
    updatedRoutines.forEach((r, i) => {
      r.order = i + 1;
    });
    
    setScheduledRoutines(updatedRoutines);
    updateScheduleRoutines(updatedRoutines);
  };
  
  // Move a routine down in the schedule
  const moveRoutineDown = (index) => {
    if (index === scheduledRoutines.length - 1) return;
    
    const updatedRoutines = [...scheduledRoutines];
    const temp = updatedRoutines[index];
    updatedRoutines[index] = updatedRoutines[index + 1];
    updatedRoutines[index + 1] = temp;
    
    // Update order
    updatedRoutines.forEach((r, i) => {
      r.order = i + 1;
    });
    
    setScheduledRoutines(updatedRoutines);
    updateScheduleRoutines(updatedRoutines);
  };
  
  // Update the routines in the current schedule
  const updateScheduleRoutines = (updatedRoutines) => {
    const updatedSchedules = schedules.map(schedule => {
      if (schedule.id === currentSchedule) {
        return {
          ...schedule,
          routines: updatedRoutines.map(r => ({
            id: r.id,
            order: r.order
          }))
        };
      }
      return schedule;
    });
    
    setSchedules(updatedSchedules);
  };
  
  // Save schedules to the event
  const saveSchedulesToEvent = async (schedulesToSave) => {
    if (!event) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    
    try {
      // In a real implementation, you would save this to your API
      // For now, we'll just simulate a successful save
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSaving(false);
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving schedules:', err);
      setIsSaving(false);
      setSaveError('Failed to save schedule. Please try again.');
    }
  };
  
  // Download audio files in schedule order
  const downloadScheduledAudio = async () => {
    if (!scheduledRoutines.length) {
      alert('No routines in the schedule');
      return;
    }
    
    try {
      const zip = new JSZip();
      const audioFolder = zip.folder('scheduled_audio');
      
      // Add each audio file to the zip in order
      const audioPromises = scheduledRoutines
        .filter(routine => routine.audioUrl)
        .map(async (routine, index) => {
          try {
            const response = await fetch(routine.audioUrl);
            const blob = await response.blob();
            
            // Create filename with order number and routine details
            const fileName = `${routine.order.toString().padStart(3, '0')}_${routine.studioName}_${routine.name}_${routine.category || 'No Category'}.mp3`;
            
            audioFolder.file(fileName, blob);
            return true;
          } catch (err) {
            console.error(`Error downloading audio for routine ${routine.name}:`, err);
            return false;
          }
        });
      
      await Promise.all(audioPromises);
      
      // Generate and save the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      const scheduleName = schedules.find(s => s.id === currentSchedule)?.name || 'schedule';
      saveAs(content, `${event.city}_${scheduleName}_audio.zip`);
      
    } catch (err) {
      console.error('Error creating zip file:', err);
      alert('Failed to download audio files. Please try again.');
    }
  };
  
  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate total duration of scheduled routines
  const getTotalDuration = () => {
    const totalSeconds = scheduledRoutines.reduce((total, routine) => {
      return total + (routine.duration || 0);
    }, 0);
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    return `${hours}h ${minutes}m`;
  };
  
  // When current schedule changes, update scheduled routines
  useEffect(() => {
    if (!currentSchedule) {
      setScheduledRoutines([]);
      return;
    }
    
    const schedule = schedules.find(s => s.id === currentSchedule);
    if (!schedule) return;
    
    // Get full routine objects for the IDs in the schedule
    const scheduledRoutineIds = new Set(schedule.routines.map(r => r.id));
    
    // Get routines that are in the schedule
    const inSchedule = routines
      .filter(routine => scheduledRoutineIds.has(routine.id))
      .map(routine => {
        const scheduleItem = schedule.routines.find(r => r.id === routine.id);
        return {
          ...routine,
          order: scheduleItem.order
        };
      })
      .sort((a, b) => a.order - b.order);
    
    // Get routines that are not in the schedule
    const notInSchedule = routines.filter(routine => !scheduledRoutineIds.has(routine.id));
    
    setScheduledRoutines(inSchedule);
    setAvailableRoutines(notInSchedule);
  }, [currentSchedule, schedules, routines]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Schedule selector */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium">Competition Schedules</h3>
        </div>
        
        <div className="flex items-center gap-2">
          {schedules.length > 0 && (
            <select
              className="select select-bordered"
              value={currentSchedule || ''}
              onChange={(e) => setCurrentSchedule(e.target.value)}
            >
              <option value="" disabled>Select a schedule</option>
              {schedules.map(schedule => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name}
                </option>
              ))}
            </select>
          )}
          
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowNewScheduleForm(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Schedule
          </button>
        </div>
      </div>
      
      {/* New schedule form */}
      {showNewScheduleForm && (
        <div className="card bg-base-200 p-4">
          <h4 className="font-medium mb-2">Create New Schedule</h4>
          <div className="flex gap-2">
            <input
              type="text"
              className="input input-bordered flex-1"
              placeholder="Schedule name"
              value={newScheduleName}
              onChange={(e) => setNewScheduleName(e.target.value)}
            />
            <button
              className="btn btn-primary"
              onClick={createSchedule}
              disabled={!newScheduleName.trim()}
            >
              Create
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setShowNewScheduleForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Save status */}
      {saveSuccess && (
        <div className="alert alert-success">
          <span>Schedule saved successfully!</span>
        </div>
      )}
      
      {saveError && (
        <div className="alert alert-error">
          <span>{saveError}</span>
          <button 
            className="btn btn-sm btn-ghost"
            onClick={() => setSaveError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Main content */}
      {currentSchedule ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Available routines */}
          <div className="lg:col-span-1">
            <div className="card bg-base-200 h-full">
              <div className="card-body p-4">
                <h3 className="card-title text-base">
                  Available Routines ({availableRoutines.length})
                </h3>
                
                {availableRoutines.length === 0 ? (
                  <div className="text-center py-8 text-base-content/70">
                    <p>All routines have been scheduled</p>
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-[500px] pr-2">
                    <div className="space-y-2">
                      {availableRoutines.map(routine => (
                        <div
                          key={routine.id}
                          className="bg-base-100 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium">{routine.name || 'Untitled Routine'}</h4>
                              <p className="text-sm text-base-content/70">
                                {routine.studioName || 'Unknown Studio'}
                                {getUserFullName(routine.userId) && (
                                  <span> • {getUserFullName(routine.userId)}</span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {routine.category && (
                                  <span className="badge badge-sm">{routine.category}</span>
                                )}
                                <div className="flex items-center text-xs text-base-content/70">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {formatDuration(routine.duration)}
                                </div>
                              </div>
                            </div>
                            <button
                              className="btn btn-circle btn-xs btn-primary"
                              onClick={() => addRoutineToSchedule(routine.id)}
                              title="Add to schedule"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Scheduled routines */}
          <div className="lg:col-span-2">
            <div className="card bg-base-200 h-full">
              <div className="card-body p-4">
                <div className="flex justify-between items-center">
                  <h3 className="card-title text-base">
                    Scheduled Routines ({scheduledRoutines.length})
                  </h3>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-sm">
                      Total Duration: <span className="font-medium">{getTotalDuration()}</span>
                    </div>
                    
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={downloadScheduledAudio}
                      disabled={!scheduledRoutines.some(r => r.audioUrl)}
                    >
                      <FileAudio className="h-4 w-4 mr-1" />
                      Download Audio
                    </button>
                  </div>
                </div>
                
                {scheduledRoutines.length === 0 ? (
                  <div className="text-center py-12 text-base-content/70">
                    <p>No routines scheduled yet</p>
                    <p className="text-sm mt-2">Add routines from the available list</p>
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-[500px] pr-2">
                    <div className="space-y-2">
                      {scheduledRoutines.map((routine, index) => (
                        <div
                          key={routine.id}
                          className="bg-base-100 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3">
                              <div className="bg-primary/10 rounded-full w-8 h-8 flex items-center justify-center">
                                <span className="font-medium text-primary">{routine.order}</span>
                              </div>
                              
                              <div>
                                <h4 className="font-medium">{routine.name || 'Untitled Routine'}</h4>
                                <p className="text-sm text-base-content/70">
                                  {routine.studioName || 'Unknown Studio'}
                                  {getUserFullName(routine.userId) && (
                                    <span> • {getUserFullName(routine.userId)}</span>
                                  )}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  {routine.category && (
                                    <span className="badge badge-sm">{routine.category}</span>
                                  )}
                                  <div className="flex items-center text-xs text-base-content/70">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {formatDuration(routine.duration)}
                                  </div>
                                  {routine.audioUrl ? (
                                    <span className="text-xs text-success">Has Audio</span>
                                  ) : (
                                    <span className="text-xs text-error">No Audio</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <button
                                className="btn btn-circle btn-xs btn-ghost"
                                onClick={() => moveRoutineUp(index)}
                                disabled={index === 0}
                                title="Move up"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                              
                              <button
                                className="btn btn-circle btn-xs btn-ghost"
                                onClick={() => moveRoutineDown(index)}
                                disabled={index === scheduledRoutines.length - 1}
                                title="Move down"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                              
                              <button
                                className="btn btn-circle btn-xs btn-error"
                                onClick={() => removeRoutineFromSchedule(routine.id)}
                                title="Remove from schedule"
                              >
                                <Trash className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-base-200 rounded-lg">
          <p className="text-base-content/70">
            {schedules.length === 0
              ? 'No schedules created yet'
              : 'Select a schedule from the dropdown'}
          </p>
          {schedules.length === 0 && (
            <button
              className="btn btn-primary btn-sm mt-4"
              onClick={() => setShowNewScheduleForm(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Create First Schedule
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleTab; 